import React from 'react';
import type { RollLine } from '@root/lib';
import { useImageUrl } from '@shared/components/ImageContext';
import { formatMoney } from '@shared/lib';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import styles from './RunList.module.scss';

interface RollAuditDialogProps {
  roll: RollLine | null;
  symbol: string;
  onClose: () => void;
}

const time = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : '—';

// Everything the server recorded about one roll, for a player who checks.
const RollAuditDialog: React.FC<RollAuditDialogProps> = ({
  roll,
  symbol,
  onClose,
}) => {
  const { t } = useTranslation();
  const { img, imgProps } = useImageUrl();

  if (!roll) return null;

  const money = (value: number): string => formatMoney(symbol, value);
  const credit = (free: boolean): string => (free ? '1' : '0');

  const rows: [string, string][] = [
    [t('Round ID'), roll.roundId],
    [t('Roll source'), roll.auto ? t('Automatic') : t('Manual')],
    [t('Die result'), String(roll.dice)],
    [t('Position'), `${roll.from} → ${roll.to}`],
    [t('Attempted position'), String(roll.attempted)],
    [t('Charge'), money(roll.charge)],
    [t('Reward'), money(roll.reward)],
    [
      t('Free credit'),
      `${credit(roll.freeBefore)} → ${credit(roll.freeAfter)}`,
    ],
  ];

  const times: [string, string][] = [
    [t('Turn deadline'), time(roll.deadline)],
    [t('Roll accepted'), time(roll.acceptedAt)],
  ];

  return (
    <div className={styles.filterScrim}>
      <div
        className={cn(styles.filterDialog, styles.auditDialog)}
        style={
          { '--panel-skin': `url(${img('PopupFrame')})` } as React.CSSProperties
        }
      >
        <div className={styles.filterHead}>
          <h2 className={styles.filterTitle}>
            {t('Roll {{number}}', { number: roll.number })}
          </h2>
          <button
            type="button"
            className={styles.close}
            aria-label={t('Close')}
            onClick={onClose}
          >
            <img {...imgProps('Icon_Close')} alt="" />
          </button>
        </div>

        {rows.map(([label, value]) => (
          <div className={styles.auditRow} key={label}>
            <span className={styles.auditLabel}>{label}</span>
            <span className={styles.auditValue}>{value}</span>
          </div>
        ))}

        <p className={styles.summary}>
          {new Date(roll.acceptedAt).toLocaleDateString(undefined, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })}
        </p>

        {times.map(([label, value]) => (
          <div className={styles.auditRow} key={label}>
            <span className={styles.auditLabel}>{label}</span>
            <span className={styles.auditValue}>{value}</span>
          </div>
        ))}

        {/* Only the overshoot has approved wording; the rest read plainly
            enough from the rows above. */}
        {roll.outcome === 'OVERSHOOT' && (
          <p className={styles.messageBody}>
            {t(
              'The roll overshot 12. Position stays {{position}} and the tile does not trigger again.',
              { position: roll.to }
            )}
          </p>
        )}
      </div>
    </div>
  );
};

export default RollAuditDialog;
